import { ref, watch, onScopeDispose } from "vue";
import { useTableHelper } from "../useTableHelper";
import { errorMessage, type GeneratedTableConfig } from "./contracts";
import type { PaginationResult } from "../../../cbi-generator/generated/dto/PaginationResult";

export function useGeneratedTable<Row extends object, Params extends object>(
  config: GeneratedTableConfig<Row, Params>,
) {
  const error = ref("");
  let requestVersion = 0;
  let disposed = false;
  let lastResult: PaginationResult<Row> = {
    items: [],
    page: 1,
    perPage: 20,
    totalCount: 0,
    totalPages: 0,
  };
  onScopeDispose(() => {
    disposed = true;
    ++requestVersion;
  });
  const table = useTableHelper<Row, Params, typeof config.resultMeta>({
    id: config.id,
    itemValue: config.rowKey,
    meta: config.resultMeta,
    defaultFilter: config.initialFilter(),
    defaultSortBy: config.defaultSort,
    showByDefault: config.visibleColumns,
    useServerSort: true,
    loadItemsServerPaginated: async (pagination, params, exporting) => {
      if (exporting)
        return config.search(params, pagination.page, pagination.perPage);
      const request = ++requestVersion;
      error.value = "";
      try {
        const result = await config.search(
          params,
          pagination.page,
          pagination.perPage,
        );
        if (request === requestVersion && !disposed) lastResult = result;
        return result;
      } catch (cause) {
        if (request === requestVersion && !disposed)
          error.value = errorMessage(cause);
        // Keep the previous rows visible; the explicit alert distinguishes them from a successful refresh.
        return lastResult;
      }
    },
  });
  watch(
    table.filter,
    () => {
      if (table.pagination.value.page !== 1) table.pagination.value.page = 1;
      else void table.reloadItems();
    },
    { deep: true },
  );
  watch(
    table.sortBy,
    () => {
      if (table.pagination.value.page !== 1) table.pagination.value.page = 1;
    },
    { deep: true },
  );
  return { table, error };
}
